import React, { useState } from 'react';
import EnhancedResultsPage from './EnhancedResultsPage';
import LandingPage from './LandingPage';

function App() {
  const params = new URLSearchParams(window.location.search);
  const [currentView, setCurrentView] = useState(
    params.get('skip') === 'true' ? 'info' : 'start'
  );
  const [sessionId, setSessionId] = useState('');
  const [currentItem, setCurrentItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    age: ''
  });

  const startAssessment = async (patientData) => {
    try {
      setLoading(true);

      const response = await fetch('/api/assessment/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: 'web_user' })
      });

      if (!response.ok) {
        throw new Error('Failed to start assessment');
      }

      const data = await response.json();
      setSessionId(data.session_id);
      setCurrentItem(data.first_item);
      setCurrentView('assessment');
    } catch (error) {
      console.error('Error starting assessment:', error);
      alert('Failed to start assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async (response) => {
    if (!currentItem) return;

    try {
      setLoading(true);

      const result = await fetch('/api/assessment/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          item_id: currentItem.item_id,
          response: response
        })
      });

      if (!result.ok) {
        throw new Error('Failed to submit response');
      }

      const data = await result.json();

      if (data.completed) {
        setCurrentView('enhanced-results');
      } else {
        setCurrentItem(data.next_item);
      }
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cameFromExternal = params.get('skip') === 'true';

  const resetApp = () => {
    if (cameFromExternal) {
      window.location.href = 'https://mental-health-metrics.netlify.app/';
    } else {
      setCurrentView('start');
      setSessionId('');
      setCurrentItem(null);
      setFormData({ name: '', age: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    startAssessment(formData);
  };

  if (currentView === 'start') {
    return <LandingPage onStartAssessment={() => setCurrentView('info')} />;
  }

  if (currentView === 'info') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f0fdfa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          maxWidth: '400px',
          width: '100%'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1a202c',
            marginBottom: '1rem'
          }}>
            Before We Start
          </h2>

          <p style={{ color: '#4a5568', marginBottom: '1.5rem' }}>
            Please provide some basic information (all fields optional):
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.25rem'
              }}>
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
                placeholder="Your name"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.25rem'
              }}>
                Age
              </label>
              <input
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
                placeholder="Your age"
              />
            </div>

            <div style={{ paddingTop: '1rem' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  backgroundColor: loading ? '#9ca3af' : '#0d9488',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Starting...' : 'Start Assessment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (currentView === 'assessment') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f0fdfa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          maxWidth: '700px',
          width: '100%'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #0d9488',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <p style={{ marginTop: '1rem', color: '#4a5568' }}>
                Processing your response...
              </p>
            </div>
          ) : currentItem ? (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#0d9488',
                    fontWeight: '500'
                  }}>
                    PAN-CAT Assessment
                  </span>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Adaptive Test
                  </span>
                </div>

                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1a202c',
                  lineHeight: '1.5'
                }}>
                  {currentItem.question}
                </h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {currentItem.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => submitResponse(index)}
                    style={{
                      width: '100%',
                      padding: '16px',
                      textAlign: 'left',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '1rem'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.borderColor = '#5eead4';
                      e.target.style.backgroundColor = '#f0fdfa';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.backgroundColor = 'white';
                    }}
                  >
                    <span style={{
                      fontWeight: '500',
                      color: '#0d9488',
                      marginRight: '12px'
                    }}>
                      {index + 1}.
                    </span>
                    {option}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#4a5568' }}>Loading question...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (currentView === 'enhanced-results') {
    return (
      <EnhancedResultsPage
        sessionId={sessionId}
        onStartNew={resetApp}
      />
    );
  }

  return <div>Loading...</div>;
}

export default App;
